import { HttpClient } from '@wix/sdk-types';
import {
  AddDeliveryRegionOptions,
  AddDeliveryRegionResponse,
  AddDeliveryRegionResponseNonNullableFields,
  BulkCreatePickupLocationResponse,
  BulkCreatePickupLocationResponseNonNullableFields,
  BulkDeletePickupLocationResponse,
  BulkDeletePickupLocationResponseNonNullableFields,
  BulkUpdatePickupLocationResponse,
  BulkUpdatePickupLocationResponseNonNullableFields,
  PickupLocation,
  PickupLocationNonNullableFields,
  PickupLocationsQueryBuilder,
  RemoveDeliveryRegionOptions,
  RemoveDeliveryRegionResponse,
  RemoveDeliveryRegionResponseNonNullableFields,
  UpdatePickupLocation,
  addDeliveryRegion as universalAddDeliveryRegion,
  bulkCreatePickupLocation as universalBulkCreatePickupLocation,
  bulkDeletePickupLocation as universalBulkDeletePickupLocation,
  bulkUpdatePickupLocation as universalBulkUpdatePickupLocation,
  createPickupLocation as universalCreatePickupLocation,
  deletePickupLocation as universalDeletePickupLocation,
  getPickupLocation as universalGetPickupLocation,
  queryPickupLocation as universalQueryPickupLocation,
  removeDeliveryRegion as universalRemoveDeliveryRegion,
  updatePickupLocation as universalUpdatePickupLocation,
} from './ecom-v1-pickup-location-pickup-locations.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ecom' };

export function createPickupLocation(
  httpClient: HttpClient
): CreatePickupLocationSignature {
  return (pickupLocation: PickupLocation) =>
    universalCreatePickupLocation(
      pickupLocation,
      // @ts-ignore
      { httpClient }
    );
}

interface CreatePickupLocationSignature {
  /**
   * Creates a new PickupLocation
   * @param - PickupLocation to be created
   * @returns The created PickupLocation
   */
  (pickupLocation: PickupLocation): Promise<
    PickupLocation & PickupLocationNonNullableFields
  >;
}

export function getPickupLocation(
  httpClient: HttpClient
): GetPickupLocationSignature {
  return (pickupLocationId: string) =>
    universalGetPickupLocation(
      pickupLocationId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetPickupLocationSignature {
  /**
   * Get a PickupLocation by id
   * @param - Id of the PickupLocation to retrieve
   * @returns The retrieved PickupLocation
   */
  (pickupLocationId: string): Promise<
    PickupLocation & PickupLocationNonNullableFields
  >;
}

export function updatePickupLocation(
  httpClient: HttpClient
): UpdatePickupLocationSignature {
  return (_id: string | null, pickupLocation: UpdatePickupLocation) =>
    universalUpdatePickupLocation(
      _id,
      pickupLocation,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdatePickupLocationSignature {
  /**
   * Update a PickupLocation
   * Delivery regions cannot be updated using this method, use AddDeliveryRegion and RemoveDeliveryRegion instead.
   * @param - PickupLocation ID
   * @returns The updated PickupLocation
   */
  (_id: string | null, pickupLocation: UpdatePickupLocation): Promise<
    PickupLocation & PickupLocationNonNullableFields
  >;
}

export function deletePickupLocation(
  httpClient: HttpClient
): DeletePickupLocationSignature {
  return (pickupLocationId: string) =>
    universalDeletePickupLocation(
      pickupLocationId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeletePickupLocationSignature {
  /**
   * Delete a PickupLocation
   * @param - Id of the PickupLocation to delete
   */
  (pickupLocationId: string): Promise<void>;
}

export function queryPickupLocation(
  httpClient: HttpClient
): QueryPickupLocationSignature {
  return () =>
    universalQueryPickupLocation(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryPickupLocationSignature {
  /**
   * Query PickupLocations using [WQL - Wix Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language)
   */
  (): PickupLocationsQueryBuilder;
}

export function addDeliveryRegion(
  httpClient: HttpClient
): AddDeliveryRegionSignature {
  return (
    pickupLocationId: string,
    deliveryRegionId: string,
    options: AddDeliveryRegionOptions
  ) =>
    universalAddDeliveryRegion(
      pickupLocationId,
      deliveryRegionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AddDeliveryRegionSignature {
  /**
   * Add a DeliveryRegion to a PickupLocation
   * @param - Id of the PickupLocation to add to the delivery region
   * @param - Id of the DeliveryRegion to add the PickupLocation to
   */
  (
    pickupLocationId: string,
    deliveryRegionId: string,
    options: AddDeliveryRegionOptions
  ): Promise<
    AddDeliveryRegionResponse & AddDeliveryRegionResponseNonNullableFields
  >;
}

export function removeDeliveryRegion(
  httpClient: HttpClient
): RemoveDeliveryRegionSignature {
  return (
    pickupLocationId: string,
    deliveryRegionId: string,
    options: RemoveDeliveryRegionOptions
  ) =>
    universalRemoveDeliveryRegion(
      pickupLocationId,
      deliveryRegionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RemoveDeliveryRegionSignature {
  /**
   * Remove a DeliveryRegion from a PickupLocation
   * @param - Id of the PickupLocation to add to the delivery region
   * @param - Id of the DeliveryRegion to add the PickupLocation to
   */
  (
    pickupLocationId: string,
    deliveryRegionId: string,
    options: RemoveDeliveryRegionOptions
  ): Promise<
    RemoveDeliveryRegionResponse & RemoveDeliveryRegionResponseNonNullableFields
  >;
}

export function bulkCreatePickupLocation(
  httpClient: HttpClient
): BulkCreatePickupLocationSignature {
  return (pickupLocations: PickupLocation[]) =>
    universalBulkCreatePickupLocation(
      pickupLocations,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreatePickupLocationSignature {
  /**
   * Bulk Create for new PickupLocation
   */
  (pickupLocations: PickupLocation[]): Promise<
    BulkCreatePickupLocationResponse &
      BulkCreatePickupLocationResponseNonNullableFields
  >;
}

export function bulkUpdatePickupLocation(
  httpClient: HttpClient
): BulkUpdatePickupLocationSignature {
  return (pickupLocations: PickupLocation[]) =>
    universalBulkUpdatePickupLocation(
      pickupLocations,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdatePickupLocationSignature {
  /**
   * Update a PickupLocation
   */
  (pickupLocations: PickupLocation[]): Promise<
    BulkUpdatePickupLocationResponse &
      BulkUpdatePickupLocationResponseNonNullableFields
  >;
}

export function bulkDeletePickupLocation(
  httpClient: HttpClient
): BulkDeletePickupLocationSignature {
  return (pickupLocationIds: string[]) =>
    universalBulkDeletePickupLocation(
      pickupLocationIds,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeletePickupLocationSignature {
  /**
   * Delete a PickupLocation
   */
  (pickupLocationIds: string[]): Promise<
    BulkDeletePickupLocationResponse &
      BulkDeletePickupLocationResponseNonNullableFields
  >;
}

export {
  ActionEvent,
  AddDeliveryRegionOptions,
  AddDeliveryRegionRequest,
  AddDeliveryRegionResponse,
  AddDeliveryRegionResponseNonNullableFields,
  Address,
  AddressStreetOneOf,
  ApplicationError,
  BulkCreatePickupLocationRequest,
  BulkCreatePickupLocationResponse,
  BulkCreatePickupLocationResponseNonNullableFields,
  BulkDeletePickupLocationRequest,
  BulkDeletePickupLocationResponse,
  BulkDeletePickupLocationResponseNonNullableFields,
  BulkUpdatePickupLocationRequest,
  BulkUpdatePickupLocationResponse,
  BulkUpdatePickupLocationResponseNonNullableFields,
  Condition,
  ConditionType,
  ConditionalRates,
  CreatePickupLocationRequest,
  CreatePickupLocationResponse,
  CreatePickupLocationResponseNonNullableFields,
  CursorPaging,
  CursorPagingMetadata,
  Cursors,
  DeletePickupLocationRequest,
  DeletePickupLocationResponse,
  DiffmatokyPayload,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  ErrorInformation,
  GetPickupLocationRequest,
  GetPickupLocationResponse,
  GetPickupLocationResponseNonNullableFields,
  IdentificationData,
  IdentificationDataIdOneOf,
  LogicalOperator,
  MessageEnvelope,
  Paging,
  PickupLocation,
  PickupLocationError,
  PickupLocationNonNullableFields,
  PickupLocationsQueryBuilder,
  PickupLocationsQueryResult,
  QueryPickupLocationRequest,
  QueryPickupLocationResponse,
  QueryPickupLocationResponseNonNullableFields,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RemoveDeliveryRegionOptions,
  RemoveDeliveryRegionRequest,
  RemoveDeliveryRegionResponse,
  RemoveDeliveryRegionResponseNonNullableFields,
  RestoreInfo,
  SortOrder,
  Sorting,
  StreetAddress,
  UpdatePickupLocation,
  UpdatePickupLocationRequest,
  UpdatePickupLocationResponse,
  UpdatePickupLocationResponseNonNullableFields,
  WebhookIdentityType,
} from './ecom-v1-pickup-location-pickup-locations.universal.js';
